/**
 * The tabs' channel (LLD §6.2). It carries a refreshed token in memory from
 * tab to tab (never through storage), a sign-out, and user activity for the
 * idle warning (§6.6), so a tab left in the background does not sign out a
 * user who is busy in another one.
 */
import type { SignInReason } from "./session-lost"

export type SessionMessage =
  | { kind: "token"; token: string; at: number }
  /** `reason` lets the tabs that follow explain it too (`reused`, `banned`…). */
  | { kind: "signout"; reason?: SignInReason }
  | { kind: "activity"; at: number }

const NAME = "devsms.session"
const listeners = new Set<(m: SessionMessage) => void>()

function open(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null
  const c = new BroadcastChannel(NAME)
  c.onmessage = (e: MessageEvent<SessionMessage>) => {
    for (const fn of listeners) fn(e.data)
  }
  // Node's channel (the tests) would otherwise keep the process alive.
  ;(c as { unref?: () => void }).unref?.()
  return c
}

let channel = open()

/** To every other tab; this tab's own listeners do not hear it. */
export function postSessionMessage(m: SessionMessage): void {
  channel?.postMessage(m)
}

/** Returns an unsubscribe function. */
export function onSessionMessage(fn: (m: SessionMessage) => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Test seam: a simulated tab closes its channel when the test ends. */
export function closeSessionChannel(): void {
  channel?.close()
  channel = null
}
